import type {ReactNode} from 'react'
import {useCallback} from 'react'
import {XIcon} from '@primer/octicons-react'
import {Heading, IconButton} from '@primer/react'
import {useSlots} from '@primer/react/experimental'
import styles from './Panel.module.css'
import {useDockedPanelActions, useDockedPanelFocusRefs} from '../../contexts/DockedPanelContext'
import type {DockedPanelType} from '../../contexts/DockedPanelContext'

const panelHeadings: Record<Exclude<DockedPanelType, null>, string> = {
  comments: 'Comments',
  alerts: 'Alerts',
  overview: 'Overview',
}

interface PanelHeaderProps {
  children: ReactNode
}

function PanelHeader({children}: PanelHeaderProps) {
  return <>{children}</>
}

interface PanelBodyProps {
  children: ReactNode
}

function PanelBody({children}: PanelBodyProps) {
  return <>{children}</>
}

interface PanelFooterProps {
  children: ReactNode
}

function PanelFooter({children}: PanelFooterProps) {
  return <>{children}</>
}

interface PanelProps {
  panelName: Exclude<DockedPanelType, null>
  children: ReactNode
}

/**
 * Self-contained compound component for docked side panel content.
 *
 * Renders the panel heading (derived from `panelName`), a close button with
 * managed focus refs, and `Panel.Header`, `Panel.Body`, and `Panel.Footer`
 * slot sub-components laid out in a flex column. The body region scrolls
 * while header and footer remain fixed.
 *
 * Can be rendered directly inside `DockedSidePanelContent` or portaled
 * into the docked panel's content slot via `createPortal`.
 */
function Panel({panelName, children}: PanelProps) {
  const {closeButtonRef, returnFocusRef} = useDockedPanelFocusRefs()
  const {closePanel} = useDockedPanelActions()

  const handleClose = useCallback(() => {
    const returnTarget = returnFocusRef.current
    closePanel()
    // Return focus to the element that opened the panel
    if (returnTarget) {
      requestAnimationFrame(() => returnTarget.focus())
    }
  }, [closePanel, returnFocusRef])

  const heading = panelHeadings[panelName]

  const [slots] = useSlots(children, {
    header: PanelHeader,
    body: PanelBody,
    footer: PanelFooter,
  })

  return (
    <div className="d-flex flex-column height-full">
      <div className={styles.Header}>
        <div className={styles.HeaderContent}>
          <Heading as="h3" className={styles.Heading}>
            {heading}
          </Heading>
          <IconButton
            ref={closeButtonRef}
            aria-label="Close side panel"
            icon={XIcon}
            variant="invisible"
            size="medium"
            onClick={handleClose}
          />
        </div>
        {slots.header}
      </div>
      <div className="flex-1 overflow-auto pr-p-3">{slots.body}</div>
      {slots.footer && <div className="pr-p-3">{slots.footer}</div>}
    </div>
  )
}

Panel.Header = PanelHeader
Panel.Body = PanelBody
Panel.Footer = PanelFooter

export {Panel}
